import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Snowflake, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRODUCTS, CATEGORIES, IMG } from "@/data/content";
import InovatieCrossPromo from "@/components/sections/InovatieCrossPromo";

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "copt", label: "Coapte" },
  { id: "congelat", label: "Congelate" },
];

export default function Products() {
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("Toate");

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      const typeOK = filter === "all" || p.type === filter;
      const catOK = category === "Toate" || p.category === category;
      return typeOK && catOK;
    });
  }, [filter, category]);

  const categoryNames = ["Toate", ...new Set(PRODUCTS.map((p) => p.category))];

  return (
    <div data-testid="page-products">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.bakeryAmbient} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/85 to-bg" />
        </div>
        <div className="max-w-7xl mx-auto container-px">
          <p className="eyebrow">Produse DOTAPAN</p>
          <h1 className="h-display text-4xl md:text-6xl text-text mt-4">Coapte și congelate, pentru afacerea ta.</h1>
          <p className="mt-5 text-base md:text-lg text-text-soft leading-relaxed max-w-2xl">
            De la croissant cu unt la blat de pizza, fiecare produs are un loc clar în meniul tău.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto container-px">
          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between mb-10">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`filter-type-${f.id}`}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors ${
                    filter === f.id ? "bg-gold border-gold text-[#0b0b0f]" : "border-border text-text-soft hover:border-gold hover:text-gold"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {categoryNames.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    category === c ? "bg-text border-text text-bg" : "border-border text-text-soft hover:border-gold hover:text-gold"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="product-card group h-full flex flex-col"
                data-testid={`product-${p.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium backdrop-blur ${p.type === "congelat" ? "bg-[#0b0b0f]/70 text-blue-200 border border-blue-300/30" : "bg-[#0b0b0f]/70 text-amber-200 border border-amber-300/30"}`}>
                      {p.type === "congelat" ? <Snowflake className="h-3 w-3" /> : <Flame className="h-3 w-3" />}
                      {p.type}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] bg-black/60 text-white/90 border border-white/20">
                    {p.weight}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{p.category}</p>
                  <h3 className="mt-2 font-display text-xl text-text">{p.name}</h3>
                  <p className="mt-2 text-sm text-text-soft leading-relaxed flex-1">{p.benefit}</p>
                  <Link to="/contact" className="mt-5">
                    <Button variant="outline" className="w-full rounded-full border-border hover:border-gold hover:text-gold">
                      Solicită ofertă
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-text-soft mt-10">Nu am găsit produse pentru filtrele alese.</p>
          )}
        </div>
      </section>

      <InovatieCrossPromo />
    </div>
  );
}
